"use client";

import React, { useId, useState } from "react";
import clsx from "clsx";

interface LabeledInputProps {
  label: string;
  name: string;
  type?: string;
  error?: string;
  passwordLabels?: { show: string; hide: string };
}

const LabeledInput = ({
  label,
  name,
  type = "text",
  error,
  passwordLabels,
  className,
  id,
  placeholder,
  ...props
}: LabeledInputProps & React.InputHTMLAttributes<HTMLInputElement>) => {
  const generatedId = useId();
  const inputId = id ?? `${name}-${generatedId}`;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isPasswordField = type === "password";
  const inputType = isPasswordField && isPasswordVisible ? "text" : type;

  return (
    <div className={clsx("w-full", className)}>
      <div className="relative">
        <input
          {...props}
          id={inputId}
          name={name}
          type={inputType}
          autoComplete={props.autoComplete ?? "off"}
          placeholder={placeholder ?? label}
          className={clsx(
            "block w-full rounded-md border bg-transparent px-3 py-2 text-base text-gray-900 appearance-none focus:outline-none focus:ring-0",
            isPasswordField && "pr-10",
            error
              ? "border-red-500 focus:border-red-500"
              : "border-gray-300 focus:border-slateCustom",
            props.disabled && "cursor-not-allowed opacity-60"
          )}
        />
        {isPasswordField && (
          <button
            type="button"
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            aria-label={isPasswordVisible ? passwordLabels?.hide ?? "Hide password" : passwordLabels?.show ?? "Show password"}
          >
            {isPasswordVisible ? (
              <EyeOffIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default LabeledInput;

const EyeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    id="Layer_1"
    data-name="Layer 1"
    viewBox="0 0 7.02 4.19"
    {...props}
  >
    <path
      className="fill-slateCustom"
      d="M3.23,4.19c-1.23-.05-2.39-.69-3.19-1.96-.05-.09-.06-.15,0-.25C.85,.59,2.49-.2,4.08,.04c1.27,.19,2.24,.85,2.91,1.94,.05,.08,.04,.14,0,.22-.73,1.23-2.05,1.99-3.75,1.98Zm.28-3.66c-.87,0-1.58,.7-1.57,1.57,0,.86,.71,1.56,1.57,1.56,.85,0,1.56-.71,1.56-1.56,0-.86-.7-1.57-1.56-1.57Z"
    />
    <path
      className="fill-slateCustom"
      d="M2.51,2.1c0-.55,.45-1,1-1,.55,0,1,.45,1,1,0,.55-.45,1-1,1-.55,0-1-.45-1-1Z"
    />
  </svg>
);

const EyeOffIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    id="Layer_1"
    data-name="Layer 1"
    viewBox="0 0 7.02 5.27"
    {...props}
  >
    <path
      className="fill-slateCustom"
      d="M1.93,.83c.26-.07,.52-.17,.78-.22,1.7-.33,3.34,.4,4.25,1.86,.07,.12,.07,.2,0,.31-.27,.43-.61,.8-1.01,1.12-.07,.05-.11,.05-.17,0-.25-.2-.5-.4-.75-.59-.06-.04-.07-.08-.04-.15,.31-.9-.2-1.84-1.12-2.06-.43-.1-.84-.03-1.22,.22-.06,.04-.1,.04-.16,0-.18-.15-.37-.3-.56-.44v-.04Z"
    />
    <path
      className="fill-slateCustom"
      d="M5.1,4.4c-.38,.16-.75,.27-1.13,.3-1.69,.14-2.99-.51-3.92-1.93-.07-.1-.07-.18,0-.28,.27-.44,.61-.81,1.01-1.13,.07-.05,.11-.05,.17,0,.25,.2,.5,.4,.75,.59,.07,.05,.07,.09,.04,.16-.3,.91,.21,1.83,1.15,2.05,.42,.1,.82,.01,1.18-.22,.07-.04,.11-.04,.17,0,.18,.15,.37,.29,.57,.45Z"
    />
    <path
      className="fill-slateCustom"
      d="M6.79,4.93c-.09,.11-.17,.22-.27,.33C4.42,3.62,2.34,1.98,.24,.33c.09-.11,.17-.22,.26-.33C2.6,1.65,4.69,3.29,6.79,4.93Z"
    />
    <path
      className="fill-slateCustom"
      d="M4.48,2.87c-.48-.38-.95-.74-1.41-1.11,.23-.18,.68-.16,.98,.02,.36,.23,.54,.66,.44,1.09Z"
    />
    <path
      className="fill-slateCustom"
      d="M2.55,2.4c.48,.37,.94,.74,1.41,1.11-.25,.18-.7,.16-1-.04-.35-.24-.51-.65-.41-1.06Z"
    />
  </svg>
);
